import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import {
  ANALYTICALREPORTGROUPINGRULE,
  PRIORITYLEVEL, SERVINGSIZE, STD
} from 'src/app/features/guided-order/samples/constants/sample-constants';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '../../core/app-config/app-config.service';
import { ReportGrouping } from '../enums/report-grouping.enum';
import { UsePriority } from '../enums/use-priority.enum';
import { AdditionalField } from '../models/additional-field.model';
import { AdditionalFieldDto } from '../models/bulk-sample-creation/additional-field-dto.model';
import { BulkSampleDetailsResponse } from '../models/bulk-sample-creation/bulk-sample-details-response.model';
import { BulkSampleRequest } from '../models/bulk-sample-creation/bulk-sample-request.model';
import { SampleDetails } from '../models/bulk-sample-creation/sample-details.model';
import { CurrentOrder } from '../models/current-order.model';
import { FavoriteSampleRequest } from '../models/favorite-sample-request.model';
import { OrderShippingTrackingDetail } from '../models/logistic/order-shipping-tracking-detail.model';
import { FavoriteOrderDetails } from '../models/my-orders/favorite-order-details.model';
import { AccountRequest } from '../models/order-creation/account-request.model';
import { InvoiceableItems } from '../models/order-creation/favorite-invoicable-item.model';
import { InvoiceableItemRequest } from '../models/order-creation/invoiceable-item-request.model';
import { ContractRequest, QuotationContractRequest, QuotationRequest } from '../models/order-creation/quotation-contract-request.model';
import { OrderTemplate } from '../models/order-template.model';
import { PatchUpdateRequest } from '../models/patch-update-request.model';
import { Recipient } from '../models/recipient.model';
import { Sample } from '../models/sample.model';
import { OrderShippingDetails } from '../models/shipping/shipping-details-request.model';
import { OrderShippingPackage } from '../models/shipping/shipping-package-request.model';
import { SpecificationRequest } from '../models/specification/specification-request.model';
import { Unit } from '../models/specification/units.model';
import { HelperService } from '../services/helper.service';
import { BaseService } from './base.service';
import { RecipientDto } from './order-data.service';
import { SampleFromFavoriteRequest } from '../models/add-sample-from/sample-from-request.model';
import { DocumentType } from '../enums/document-type.enum';
import { DeliveryChannel } from '../enums/delivery-channel.enum';
import { TranslationService } from 'projects/dop-core/src/public-api';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { ORDER } from 'src/app/features/guided-order/samples/constants/sample-constants';
import { ContactType } from '../enums/contact-type.enum';
import { InvoiceableItemUpdateRequest } from '../models/order-creation/invoiceable-item-update-request.model';
import { OrderType } from '../enums/order-type.enum';
import { FavoriteSampleResponse } from '../models/favorite-sample-response.model';
import { PreviousSampleResponse } from '../models/previous-sample-response.model';
import { UserAndAccountSpecificDetails } from '../models/user-and-account-specific-details';
import { ScheduleOrder } from '../models/my-orders/schedule-order-details.model';
import { DeleteSpecificationRequest } from '../models/specification/delete-specification-request.model';
import { SpecificationMultipleSampleRequest } from '../models/specification/specification-multipleSample-request.model';

@Injectable({
  providedIn: 'root'
})

export class OrderCommandService extends BaseService {

  appConfigService: AppConfigService;
  constructor(
    httpClient: HttpClient,
    appConfigService: AppConfigService,
    private helperService: HelperService,
    private messageService: MessageService) {
    super(httpClient, (appConfigService.configuration) ? appConfigService.configuration.apiConfig.orderCommandBaseUrl : '');
    this.appConfigService = appConfigService;
  }

  //#region Order
  addDraftOrder(currentOrder: CurrentOrder): Observable<any> {
    const createOrderRequest: CreateOrderRequest = { orderSource: "GuidedOrdering", labsiteCode: currentOrder.quotation.salesPartnerCode, labsiteType: "Legacy"}
    return this.post<OrderResponse>(`orders/draft`, createOrderRequest).pipe(map((data: OrderResponse) => data));
  }

  async addOrderDetails(order: CurrentOrder, callback: () => void, eleCode?: string, intercoEleCode?: string, isElimsLab = false) {
    const apiUrl = `orders/${order.OrderCode}`;
    this.saveOrderAccount({ code: order.account.code, name: order.account.name },
      { code: order.orderTemplate.code, name: order.orderTemplate.name },
      eleCode, intercoEleCode, apiUrl).toPromise();
    await this.saveOrderDetails(order.OrderName, apiUrl, order.OrderReference, order.quotation.salesPartnerCode)
    .toPromise().then((x: any) => {
      order.OrderReference = x.result;
    }).catch(error => {
      const errorMessage = this.helperService.handleHttpError(error, ORDER, false);
      this.showError({ detail: TranslationService.translate(errorMessage) });
    });
    this.saveOrderContractQuotations(order.quotation, order.contract, order.quotation.receivingLabCode,
      order.quotation.receivingLabName, order.quotation.salesPartnerCode,
      order.quotation.salesPartnerName, isElimsLab, apiUrl).toPromise();

    if (order.standardCommentTemplate && order.standardCommentTemplate.templateCode) {
      this.addOrUpdateOrderCommentTemplate(order.OrderCode, order.standardCommentTemplate.templateCode,
        order.standardCommentTemplate.templateName).toPromise();
    }

    if (callback) {
      callback();
    }
  }

  // Added to make service wait until all data is pushed to DB
  async addAllOrderDetails(order: CurrentOrder, eleCode?: string, intercoEleCode?: string, isElimsLab = false) {
    const apiUrl = `orders/${order.OrderCode}`;
    let code: any = order.OrderReference;

    if (code !== undefined) {
      await this.saveOrderAccount({ code: order.account.code, name: order.account.name },
        { code: order.orderTemplate.code, name: order.orderTemplate.name },
        eleCode, intercoEleCode, apiUrl).toPromise();

      await this.saveOrderContractQuotations(order.quotation, order.contract,
        order.quotation.receivingLabCode, order.quotation.receivingLabName,
        order.quotation.salesPartnerCode, order.quotation.salesPartnerName, isElimsLab, apiUrl).toPromise();

      await this.saveOrderDetails(order.OrderName, apiUrl, code, order.quotation.salesPartnerCode).toPromise().then((x: any) => {
        code = x.result;
      }).catch(error => {
        const errorMessage = this.helperService.handleHttpError(error, ORDER, true);
        this.showError({ detail: TranslationService.translate(errorMessage) });
      });
      if (order.standardCommentTemplate) {
        await this.addOrUpdateOrderCommentTemplate(order.OrderCode,
          order.standardCommentTemplate?.templateCode, order.standardCommentTemplate?.templateName).toPromise();
      }
      return code;
    }
  }

  showError(data: { severity?: string, summary?: string, detail: string }) {
    this.messageService.add({
      key: 'orderReferenceError',
      severity: !data.severity ? 'error' : data.severity,
      summary: data.summary,
      detail: data.detail
    });
  }

  updateOrderAccount(orderCode: string, account: AccountRequest): Observable<any> {
    const apiUrl = `orders/${orderCode}/accounts`;
    return this.put(apiUrl, account);
  }

  updateOrderQuotationContract(orderCode: string, request: QuotationContractRequest): Observable<any> {
    const apiUrl = `orders/${orderCode}/quotationContracts`;
    return this.put(apiUrl, request);
  }

  patchOrderQuotationContract(orderCode: string, quotationCode: string): Observable<any> {
    const apiUrl = `orders/${orderCode}/quotationContracts`;
    const patchRequest: PatchRequest = {
      value: quotationCode,
      op: 'replace',
      path: '/quotation/code'
    };

    return this.patch<any>(apiUrl, JSON.stringify([patchRequest]));
  }

  deleteOrder<T>(orderCode: string): Observable<T> {
    return this.delete<T>(`orders/${orderCode}`);
  }

  updateOrderName(orderCode: string, orderName: string): Observable<any> {
    const patchRequest: PatchRequest = {
      value: orderName,
      op: 'replace',
      path: '/Name'
    };

    const apiUrl = `orders/${orderCode}`;
    return this.patch<any>(apiUrl, JSON.stringify([patchRequest]));
  }

  //#endregion

  //#region Sample
  // createBulkSamples(orderCode: string, samples: Sample[]): Observable<Sample[]> {
  //   const apiUrl = `orders/${orderCode}/bulksamples`;
  //   const url = this.orderCommandApiBaseUrl + apiUrl;
  //   return this.httpClient.post<Sample[]>(url, samples);
  // }

  addBulkSamples(
    orderCode: string, sampleServiceCode: string, orderTemplate: OrderTemplate,
    sampleCodes: string[], af: AdditionalField[], samplesCount: number, sampleSpecificationDenoUnit?: Unit)
    : Observable<Sample[]> {

    const body: SampleWithAdditionalField[] = [];
    if (!sampleSpecificationDenoUnit) {
      sampleSpecificationDenoUnit = { code: '', name: '' };
    }

    const list: AdditionalFieldDto[] = [];
    af.forEach(x => {
      list.push({ code: x.code, value: this.helperService.formatAdditionalFieldValue(x.code, x.value, x.fieldType) });
    });

    let i = samplesCount + 1;
    sampleCodes.forEach(code => {
      body.push({
        sampleDetails: {
          templateCode: sampleServiceCode,
          customerReference: i === 1 ? 'New Sample' : `New Sample ${i}`,
          sampleReference: code,
          analyticalReportGroupingRule: orderTemplate.enableReportGrouping ? ReportGrouping.SAMPLE : null,
          priorityLevel: orderTemplate.enableReportGrouping || orderTemplate.usePriority !== UsePriority.NONE ? STD : null,
          servingUnitDenominatorCode: sampleSpecificationDenoUnit.code,
          servingUnitDenominatorName: sampleSpecificationDenoUnit.name,
          servingUnitNumeratorName: this.appConfigService.configuration.noUnitName,
          servingUnitNumeratorCode: this.appConfigService.configuration.noUnitCode
        },
        additionalFields: list
      });
      i++;
    });

    const resourceUrl = `orders/${orderCode}/bulksamples`;
    return this.post<BulkSampleResponse>(resourceUrl, body, true).pipe(map((x: {
      samples: {
        sampleCode?: number;
        customerReference?: string;
      }[]
    }) => {
      if (x.samples) {
        const samples: Sample[] = [];
        x.samples.forEach(element => {
          const sample1 = body.find(s => s.sampleDetails.customerReference === element.customerReference);
          const tempSample = new Sample(element.sampleCode.toString(), sample1.sampleDetails.sampleReference, element.customerReference);
          tempSample.analyticalReportGroupingRule = sample1.sampleDetails.analyticalReportGroupingRule;
          tempSample.priorityLevel = sample1.sampleDetails.priorityLevel;
          tempSample.servingUnitDenominatorCode = sampleSpecificationDenoUnit.code;
          tempSample.servingUnitDenominatorName = sampleSpecificationDenoUnit.name;
          samples.push(tempSample);
        });

        return samples;
      }
    }));
  }

  addSamplesForOrder(orderCode: string, samples: BulkSampleRequest[]): Observable<BulkSampleDetailsResponse> {
    const resourceUrl = `orders/${orderCode}/bulksamples`;
    return this.post<BulkSampleDetailsResponse>(resourceUrl, samples, true);
  }

  cloneInvoiceableItemsFromFavAndSubmiited(
    orderCode: string, referenceId: string, quotationCodes: string[], isFromFav: boolean = true): Observable<boolean> {
    const resourceUrl = `orders/${orderCode}/invoiceableItems/clone`;
    return this.post<any>(resourceUrl,
      {
        orderType: isFromFav ? OrderType.Favorite : OrderType.Submitted,
        referenceId,
        quotationCodes
      }).pipe(map(x => x && x.message && x.message.trim() !== '' ? true : false));

    // return of<boolean>(true);
  }

  cloneInvoiceableItemsPending(orderCode: string, quotationCodes: string[]): Observable<boolean> {
    const resourceUrl = `orders/${orderCode}/invoiceableItems/clone`;
    return this.post<any>(resourceUrl,
      {
        orderType: OrderType.Pending,
        quotationCodes
      }).pipe(map(x => x && x.message && x.message.trim() !== '' ? true : false));
  }

  addSamplesFromFavorites(orderCode: string, samples: SampleFromFavoriteRequest): Observable<FavoriteSampleResponse> {
    const resourceUrl = `orders/${orderCode}/addSamplesFromFavorites`;
    return this.post<any>(resourceUrl, samples, true);
  }

  addSamplesFromPrevious(orderCode: string, samples: SampleFromFavoriteRequest): Observable<PreviousSampleResponse> {
    const resourceUrl = `orders/${orderCode}/addSamplesFromPrevious`;
    return this.post<any>(resourceUrl, samples, true);
  }

  copySamples(orderCode: string, sourceSampleCode: string, samples: Sample[], copyOptions: number[])
    : Observable<Sample[]> {
    const body: CopySampleRequest = {
      sourceSampleCode,
      customerReferences: samples.map(x => x.customerReference),
      copyOptions
    };

    const resourceUrl = `orders/${orderCode}/copysamples`;
    return this.post<CopySampleResponse>(resourceUrl, body, true).pipe(map((x: {
      samples: {
        sampleCode?: string;
        customerReference?: string;
        sampleReference?: string;
      }[]
    }) => {
      if (x.samples) {
        const copiedSamples: Sample[] = [];
        x.samples.forEach(element => {
          const sample1 = samples.find(s => s.customerReference === element.customerReference);
          const tempSample = new Sample(element.sampleCode, element.sampleReference, element.customerReference);
          tempSample.analyticalReportGroupingRule = sample1.analyticalReportGroupingRule;
          tempSample.priorityLevel = sample1.priorityLevel;
          copiedSamples.push(tempSample);
        });

        return copiedSamples;
      }
    }));
  }

  addUpdateBulkSamples(
    orderCode: string, sampleServiceCode: string, sampleCodes: string[],
    samples: Sample[])
    : Observable<Sample[]> {
    const apiUrl = `orders/${orderCode}/bulksamples`;
    const sampleWithAdditionalFields: SampleWithAdditionalField[] = [];
    let i = 0;
    for (const sample1 of samples) {
      const sampleWithAdditionalField: SampleWithAdditionalField = new SampleWithAdditionalField();
      sampleWithAdditionalField.sampleDetails = new SampleDetails();
      sampleWithAdditionalField.additionalFields = [];
      sampleWithAdditionalField.sampleDetails.customerReference = sample1.customerReference;
      sampleWithAdditionalField.sampleDetails.sampleReference = sampleCodes && !sample1.sampleCode
        ? sampleCodes[i++] : sample1.eurofinsSampleCode;
      sampleWithAdditionalField.sampleDetails.templateCode = sampleServiceCode;
      sampleWithAdditionalField.sampleDetails.comment = sample1.comment,
        sampleWithAdditionalField.sampleDetails.description = sample1.description;
      sampleWithAdditionalField.sampleDetails.lotNumber = sample1.lotNumber;
      sampleWithAdditionalField.sampleDetails.priorityLevel = sample1.priorityLevel;
      sampleWithAdditionalField.sampleDetails.analyticalReportGroupingRule = sample1.analyticalReportGroupingRule;
      sampleWithAdditionalField.code = sample1.sampleCode ? Number(sample1.sampleCode) : 0;
      sampleWithAdditionalField.sampleDetails.recommendationPublication = sample1.regulation;
      sampleWithAdditionalField.sampleDetails.resultTransformation = sample1.resultConversion;
      sampleWithAdditionalField.sampleDetails.servingSize = sample1.servingSize ? Number(sample1.servingSize) : null;
      sampleWithAdditionalField.sampleDetails.servingUnitDenominatorCode = sample1.servingUnitDenominatorCode;
      sampleWithAdditionalField.sampleDetails.servingUnitDenominatorName = sample1.servingUnitDenominatorName;
      sampleWithAdditionalField.sampleDetails.servingUnitNumeratorCode = sample1.servingUnitNumeratorCode;
      sampleWithAdditionalField.sampleDetails.servingUnitNumeratorName = sample1.servingUnitNumeratorName;
      sample1.additionalFields.forEach(a => {
        sampleWithAdditionalField.additionalFields.push(new AdditionalFieldDto(a.code,
          this.helperService.formatAdditionalFieldValue(a.code, a.value, a.fieldType)));
      });
      sampleWithAdditionalFields.push(sampleWithAdditionalField);
    }
    return this.put<BulkSampleResponse>(apiUrl, sampleWithAdditionalFields, true).pipe(map((x: {
      samples: {
        sampleCode?: number;
        customerReference?: string;
      }[]
    }) => {
      if (x.samples) {
        const newSample: Sample[] = [];
        x.samples.forEach(element => {
          const sample1 = sampleWithAdditionalFields.find(s => s.sampleDetails.customerReference === element.customerReference);
          const tempSample = new Sample(element.sampleCode.toString(), sample1.sampleDetails.sampleReference, element.customerReference);
          tempSample.analyticalReportGroupingRule = sample1.sampleDetails.analyticalReportGroupingRule;
          tempSample.priorityLevel = sample1.sampleDetails.priorityLevel;
          newSample.push(tempSample);
        });

        return newSample;
      }
    }));
  }

  addUpdateRecipient(orderCode: string, recipients: Recipient[])
    : Observable<Recipient[]> {
    const apiUrl = `orders/${orderCode}/contacts`;
    return this.put<any>(apiUrl, recipients.map(x => {
      const informationToSend: {
        documentType: DocumentType,
        deliveryChannel?: DeliveryChannel,
        language?: string,
        noOfCopy?: string
      }[] = [];

      if (x.invoice) {
        informationToSend.push({ documentType: DocumentType.InvoiceDocument });
      }

      if (x.contactType === ContactType.Configured) {
        if (x.analyticalReport) {
          informationToSend.push({
            documentType: (DocumentType.AnalyticalReport)
          });
        }
        if (x.analyticalReportCopy) {
          informationToSend.push({
            documentType: (DocumentType.AnalyticalReportCopy)
          });
        }
      } else {
        if (x.analyticalReportCopy || x.analyticalReport) {
          informationToSend.push({
            documentType: (DocumentType.AnalyticalReportCopy)
          });
        }
      }

      const dto: RecipientDto = {
        email: x.email,
        code: x.code,
        firstName: x.firstName,
        id: x.id,
        lastName: x.lastName,
        informationToSend,
        isReadOnly: x.isReadOnly,
        contactType: x.contactType
      };

      return dto;
    })).pipe(map(x => {

      if (recipients.every(xy => xy.contactType === ContactType.Configured && !xy.id)) {
        // first time loading nims recipients and adding in db
        for (let index = 0; index < x.contacts.length; index++) {
          recipients[index].id = x.contacts[index].id;
        }
      } else {
        x.contacts.forEach((element: any) => {
          const r = recipients.find(xy => element.email === xy.email && !xy.id);
          if (r) {
            r.id = element.id;
          }
        });
      }

      recipients.forEach(element => {
        element.informationToSend = [];
        if (element.analyticalReportCopy) {
          element.informationToSend.push({ documentType: DocumentType.AnalyticalReport });
        }

        if (element.analyticalReport) {
          element.informationToSend.push({ documentType: DocumentType.AnalyticalReportCopy });
        }

        if (element.invoice) {
          element.informationToSend.push({ documentType: DocumentType.InvoiceDocument });
        }
      });

      return recipients;
    }));
  }

  deleteRecipient(orderCode: string, id: string) {
    return this.delete(`orders/${orderCode}/contacts/${id}`);
  }

  deleteFavoriteSample(id: string) {
    return this.delete(`favoriteSamples/${id}`);
  }

  patchSampleDetails(sampleCode: string, propertyName: string, updatedValue: any): Observable<any> {
    if (!sampleCode) {
      throw new Error(`The parameter 'sampleCode' must be defined.`);
    }

    if (!propertyName) {
      throw new Error(`The parameter 'propertyName' must be defined.`);
    }
    const body: PatchRequest[] = [{
      value: updatedValue,
      op: 'replace',
      path: `/${propertyName}`,
    }];

    const resourceUrl = `samples/${sampleCode}/details`;
    return this.patch<any>(resourceUrl, JSON.stringify(body));
  }

  patchOrderDetails(orderCode: string, updatedProperties: { name: string, value: string }[]): Observable<any> {
    const body: PatchRequest[] = [];
    updatedProperties.forEach(property => {
      body.push({
        value: property.value,
        op: 'replace',
        path: `/${property.name}`,
      });
    });

    const resourceUrl = `orders/${orderCode}`;
    return this.patch<any>(resourceUrl, JSON.stringify(body));
  }

  validateOrder(orderCode: string) {
    const resourceUrl = `orders/${orderCode}/transitions`;
    return this.post(resourceUrl);
  }

  patchBulkSample(sampleCodes: string[], colName: string, value: string, priorityValue: string = STD, updatePriorityValue: boolean = true) {
    if (colName === SERVINGSIZE) {
      value = value;
    }
    const body: { request: SamplePatchRequest[] } = { request: [] };
    sampleCodes.forEach(code => {
      if (colName === ANALYTICALREPORTGROUPINGRULE && updatePriorityValue) {
        body.request.push({
          colName: PRIORITYLEVEL,
          sampleCode: code,
          optype: 'replace',
          value: priorityValue
        });
      }
      body.request.push({
        colName,
        sampleCode: code,
        optype: 'replace',
        value
      });
    });

    const resourceUrl = `samples`;
    return this.patch(resourceUrl, body);
  }

  patchBulkSampleWithRequest(samplePatchRequest: SamplePatchRequest[]) {
    if (!samplePatchRequest) {
      throw new Error(`The parameter 'samplePatchRequest' must be defined.`);
    }
    const body = { request: samplePatchRequest };
    const resourceUrl = `samples`;
    return this.patch(resourceUrl, body);
  }

  patchInvoiceableItemForSample(sampleCode: string, invoiceableItemRequest: InvoiceableItemUpdateRequest) {
    if (!invoiceableItemRequest) {
      throw new Error(`The parameter 'invoiceableItemRequest' must be defined.`);
    }
    const resourceUrl = `samples/` + sampleCode + `/invoiceableItems`;
    return this.patch(resourceUrl, invoiceableItemRequest);
  }

  deleteSample<T>(sampleCode: string, caller?: string): Observable<T> {
    const apiUrl = `samples/${sampleCode}`;
    if (caller) {
      return this.deleteFromQuery(apiUrl, { params: { caller } }) as Observable<T>;
    }
    return this.delete<T>(apiUrl);
  }

  deleteAllSamples<T>(orderCode: string, caller?: string): Observable<T> {
    const apiUrl = `orders/${orderCode}/samples`;
    if (caller) {
      return this.deleteFromQuery(apiUrl, { params: { caller } }) as Observable<T>;
    }
    return this.delete<T>(apiUrl);
  }

  removeSampleInvoiceableItems<T>(sampleCode: string): Observable<T> {
    const apiUrl = `samples/${sampleCode}/invoiceableItems`;
    return this.delete<T>(apiUrl);
  }

  removeInvoiceableItemsForSamples<T>(sampleCodes: string[]): Observable<T> {
    const allSampleCodes = sampleCodes.join(',');
    const apiUrl = `samples/invoiceableItems?sampleCodes=${allSampleCodes}`;
    return this.delete<T>(apiUrl);
  }

  removePlanningLines<T>(planningLineIds: number[]): Observable<T> {
    const allplanningLineIds = planningLineIds.join(',');
    const apiUrl = `samples/planningLines?planningLineIds=${allplanningLineIds}`;
    return this.delete<T>(apiUrl);
  }

  removeSampleInvoiceableItemForSubgroupQl(
    sampleCode: string, quotationCode: string, subGroupCode: string, quotationLinesCode: string): Observable<any> {
    const options = {
      params: {
        quotationLineCode: quotationLinesCode
      }
    };
    const apiUrl = `samples/${sampleCode}/quotations/${quotationCode}/subGroups/${subGroupCode}/quotationLines`;
    return this.deleteFromQuery(apiUrl, options);
  }

  removeSampleInvoiceableItemForCustomerPackage<T>(sampleCode: string, quotationCode: string, customerPackageCode: string): Observable<T> {
    const apiUrl = `samples/${sampleCode}/quotations/${quotationCode}/customerPackages/${customerPackageCode}`;
    return this.delete<T>(apiUrl);
  }


  removeSampleInvoiceableItemForTestQl<T>(sampleCode: string, quotationCode: string, quotationLineCode: string): Observable<T> {
    const apiUrl = `samples/${sampleCode}/quotations/${quotationCode}/quotationLines/${quotationLineCode}`;
    return this.delete<T>(apiUrl);
  }

  removeSampleInvoiceableItemForPackageQl<T>(sampleCode: string, quotationCode: string, quotationLineCode: string): Observable<T> {
    const apiUrl = `samples/${sampleCode}/quotations/${quotationCode}/quotationLines/${quotationLineCode}`;
    return this.delete<T>(apiUrl);
  }


  removeSampleInvoiceableItemForSCPackage<T>(sampleCode: string, salesCatalogueCode: string, packageCode: string): Observable<T> {
    const apiUrl = `samples/${sampleCode}/salesCatalogues/${salesCatalogueCode}/packages/${packageCode}`;
    return this.delete<T>(apiUrl);
  }

  removeSampleInvoiceableItemForSCTest<T>(sampleCode: string, salesCatalogueCode: string, testCode: string): Observable<T> {
    const apiUrl = `samples/${sampleCode}/salesCatalogues/${salesCatalogueCode}/tests/${testCode}`;
    return this.delete<T>(apiUrl);
  }

  removeSampleInvoiceableItemForTestBundleTests<T>(sampleCode: string, salesCatalogueCode: string, testBundleCode: string, testCode: string)
    : Observable<T> {
    const apiUrl = `samples/${sampleCode}/salesCatalogues/${salesCatalogueCode}/testBundles/${testBundleCode}/tests?testCode=${testCode}`;
    return this.delete<T>(apiUrl);
  }

  removeOrderInvoiceableItemForSCPackage<T>(orderCode: string, salesCatalogueCode: string, packageCode: string): Observable<T> {
    const apiUrl = `orders/${orderCode}/salesCatalogues/${salesCatalogueCode}/packages/${packageCode}`;
    return this.delete<T>(apiUrl);
  }

  removeOrderInvoiceableItemForSCTest<T>(orderCode: string, salesCatalogueCode: string, testCode: string): Observable<T> {
    const apiUrl = `orders/${orderCode}/salesCatalogues/${salesCatalogueCode}/tests/${testCode}`;
    return this.delete<T>(apiUrl);
  }

  removeOrderInvoiceableItemForTestBundleTests<T>(orderCode: string, salesCatalogueCode: string, testBundleCode: string, testCode: string)
    : Observable<T> {
    const apiUrl = `orders/${orderCode}/salesCatalogues/${salesCatalogueCode}/testBundles/${testBundleCode}/tests?testCode=${testCode}`;
    return this.delete<T>(apiUrl);
  }
  //#endregion
  //#region Additional Field
  patchBulkAdditionalField(list: { sampleCode: string, code: string, fieldType: string }[], afValue: string) {
    const body: { request: AdditionalFieldPatchRequest[] } = { request: [] };
    list.forEach(code => {
      body.request.push({
        colName: 'value',
        additionalFieldCode: code.code,
        sampleCode: code.sampleCode,
        optype: 'replace',
        value: this.helperService.formatAdditionalFieldValue(code.code, afValue, code.fieldType)
      });
    });

    const resourceUrl = `samples/additionalFields`;
    return this.patch(resourceUrl, body);
  }

  patchSampleAdditionalField(sampleCode: string, af: AdditionalField)
    : Observable<any> {
    if (!sampleCode) {
      throw new Error(`The parameter 'sampleCode' must be defined.`);
    }
    if (!af || !af.code) {
      throw new Error(`The parameter 'additionalFieldCode' must be defined.`);
    }
    const body: PatchRequest[] = [{
      value: this.helperService.formatAdditionalFieldValue(af.code, af.value, af.fieldType),
      op: 'replace',
      path: '/value'
    }];

    const resourceUrl = `samples/${sampleCode}/additionalFields/${af.code}`;
    return this.patch<any>(resourceUrl, body);
  }

  deleteAfFromAllSample<T>(orderCode: string, afs: AdditionalField[]): Observable<T> {
    const apiUrl = `orders/${orderCode}/samples/additionalFields`;
    const afsDto: string[] = [];
    afs.forEach(x => afsDto.push(x.code));
    return this.delete<T>(apiUrl, afsDto);
  }

  deleteAfFromOrder<T>(orderCode: string, additionalFieldCode: string): Observable<T> {
    const apiUrl = `orders/${orderCode}/additionalFields/${additionalFieldCode}`;
    return this.delete<T>(apiUrl);
  }

  addOptionalSamplesAf(orderCode: string, adfs: AdditionalFieldDto[])
    : Observable<any> {
    const apiUrl = `orders/${orderCode}/samples/additionalFields`;
    return this.post<any>(apiUrl, adfs);
  }

  addOrderAfs(orderCode: string, adfs: AdditionalFieldDto[])
    : Observable<any> {
    const apiUrl = `orders/${orderCode}/additionalFields`;
    return this.put<any>(apiUrl, adfs);
  }

  patchOrderAdditionalField(orderCode: string, af: AdditionalField)
    : Observable<any> {
    if (!orderCode) {
      throw new Error(`The parameter 'orderCode' must be defined.`);
    }
    if (!af || !af.code) {
      throw new Error(`The parameter 'additionalFieldCode' must be defined.`);
    }
    const body: PatchRequest[] = [{
      value: this.helperService.formatAdditionalFieldValue(af.code, af.value, af.fieldType),
      op: 'replace',
      path: '/value'
    }];

    const resourceUrl = `orders/${orderCode}/additionalFields/${af.code}`;
    return this.patch<any>(resourceUrl, body);
  }

  //#endregion

  //#region Analysis
  addAnalysisToSample(sampleCode: string, invoiceableItems: InvoiceableItemRequest): Observable<any> {
    const apiUrl = `samples/${sampleCode}/invoiceableItems`;
    return this.put(apiUrl, invoiceableItems);
  }

  patchAnalysisToSample(sampleCode: string, invoiceableItems: InvoiceableItemRequest): Observable<any> {
    const apiUrl = `samples/${sampleCode}/invoiceableItems`;
    return this.patch(apiUrl, invoiceableItems);
  }


  bulkAddInvoiceableItem(requestModel: InvoiceableItemRequest): Observable<any> {
    const apiUrl = `samples/invoiceableItems`;
    return this.post(apiUrl, requestModel);
  }

  addOrUpdateFavoriteAnalysis(quotationCode: string, favAnalysis: InvoiceableItems): Observable<any> {
    return this.post(`quotations/${quotationCode}/favoriteAnalyses`, favAnalysis);
  }
  //#endregion

  //#region Specifications
  saveSpecifications(sampleCode: string, specifications: SpecificationRequest[]): Observable<any> {
    const apiUrl = `samples/${sampleCode}/specifications`;
    return this.post(apiUrl, specifications);
  }

  saveSpecificationsMultipleSamples(specifications: SpecificationMultipleSampleRequest): Observable<any> {
    const apiUrl = `specifications`;
    return this.post(apiUrl, specifications);
  }

  deleteSpecifications(specificationIds: string[]): Observable<any> {
    const apiUrl = `samples/specifications`;
    return this.delete(apiUrl, specificationIds);
  }

  deleteSpecificationsMultipleSamples(deletespecification: DeleteSpecificationRequest): Observable<any> {
    const apiUrl = `specifications`;
    return this.delete(apiUrl, deletespecification);
  }
  //#endregion Specifications

  // #region FavOrder

  saveFavOrder(favOrderDetails: FavoriteOrderDetails) {
    const apiUrl = `orders/favoriteOrder`;
    return this.post(apiUrl, favOrderDetails);
  }

  deleteFavoriteOrder<T>(favorderOrderCode: string): Observable<T> {
    return this.delete<T>(`favoriteOrder/${favorderOrderCode}`);
  }

  //#endregion FavOrder


  // #region Recurrent Order

  scheduleRecurrentOrder(recurrentOrder: ScheduleOrder): Observable<any>{
    const apiUrl = `recurrentOrdersSchedule`;
    return this.post(apiUrl, recurrentOrder);
  }

  deleteScheduleOrder(scheduleId: number): Observable<any>{
    const apiUrl = `recurrentOrdersSchedule/${scheduleId}`;
    return this.delete(apiUrl);
  }

  //#endregion Recurrent Order

  //#region Shipping

  addOrUpdateShippingDetails(orderCode: string, orderShippingDetail: OrderShippingDetails): Observable<any> {
    const apiUrl = `orders/${orderCode}/shipping/details`;
    return this.post(apiUrl, orderShippingDetail);
  }

  deleteShippingDetails(orderCode: string): Observable<any> {
    const apiUrl = `orders/${orderCode}/shipping`;
    return this.delete(apiUrl);
  }

  addNewPackage(orderCode: string, orderShippingPackage: OrderShippingPackage): Observable<any> {
    const apiUrl = `orders/${orderCode}/shipping/package`;
    return this.post(apiUrl, orderShippingPackage);
  }

  updateShippingTracking(orderCode: string, shippingTracking: OrderShippingTrackingDetail): Observable<any> {
    const apiUrl = `orders/${orderCode}/shipping/tracking-detail`;
    return this.post(apiUrl, shippingTracking);
  }

  patchPackageDetails(packageId: string, updatedProperties: PatchUpdateRequest[]): Observable<any> {
    if (!packageId) {
      throw new Error(`The parameter 'packageId' must be defined.`);
    }

    const body: PatchRequest[] = [];
    updatedProperties.forEach(property => {
      body.push({
        value: property.updatedValue,
        op: 'replace',
        path: `/${property.propertyName}`,
      });
    });

    const resourceUrl = `orders/shipping/package/${packageId}`;
    return this.patch<any>(resourceUrl, body);
  }

  patchAddress(addressId: number, updatedProperties: PatchUpdateRequest[]): Observable<any> {

    const body: PatchRequest[] = [];
    updatedProperties.forEach(property => {
      body.push({
        value: property.updatedValue,
        op: 'replace',
        path: `/${property.propertyName}`,
      });
    });

    const apiUrl = `orders/shipping/pickup/address/${addressId}`;
    return this.patch<any>(apiUrl, body);
  }

  patchPickup(pickupId: string, updatedProperties: PatchUpdateRequest[]): Observable<any> {

    const body: PatchRequest[] = [];
    updatedProperties.forEach(property => {
      body.push({
        value: property.updatedValue,
        op: 'replace',
        path: `/${property.propertyName}`,
      });
    });

    const apiUrl = `orders/shipping/pickup/${pickupId}`;
    return this.patch<any>(apiUrl, body);
  }

  patchShippingDetails(orderCode: string, updatedProperties: PatchUpdateRequest[]): Observable<any> {
    const apiUrl = `orders/shipping/details/${orderCode}`;
    return this.buildPatchRequest(apiUrl, updatedProperties);
  }

  deletePackage(packageId: string): Observable<any> {
    const apiUrl = `orders/shipping/package/${packageId}`;
    return this.delete(apiUrl);
  }

  //#endregion Shipping


  addFavoriteSample(sampleId: string, favoriteSample: FavoriteSampleRequest): Observable<any> {
    return this.post(`favorite/samples/${sampleId}`, favoriteSample);
  }

  updateFavoriteSample(sampleId: string, favSampleId: string, favoriteSample: FavoriteSampleRequest): Observable<any> {
    return this.put(`favoriteSamples/${favSampleId}/samples/${sampleId}`, favoriteSample);
  }

  updateUserAndAccountSpecificDetails(userAndAccountSpecificDetails: UserAndAccountSpecificDetails): Observable<any>{
    return this.post(`userAndAccountSpecificDetails`, userAndAccountSpecificDetails);
  }

  //#region httpClient methods


  private saveOrderAccount(
    account: { code: string, name: string }, orderTemplate: { code: string, name: string },
    eleCode: string, intercoEleCode: string, apiUrl: string) {
    const body: AccountRequest = {
      accountCode: account.code,
      accountName: account.name,
      orderingServiceCode: orderTemplate.code,
      orderingServiceName: orderTemplate.name,
      eleCode,
      intercoEleCode
    };

    return this.put(apiUrl + `/accounts`, body);
  }

  private saveOrderDetails(orderName: string, apiUrl: string, code: string, salesPartnerCode: string) {
    if(orderName == undefined || orderName.trim() == ''){
      orderName = null;
    }
    const body = {
      name: orderName,
      orderReference: code
    };

    // this.currentOrderService.currentOrder.OrderReference = code;
    return this.put(apiUrl + `/labsites/${salesPartnerCode}`, body);
  }

  private saveOrderContractQuotations(
    quotation: QuotationRequest, contract: ContractRequest, receivingLabCode: string, receivingLabName: string, salesPartnerCode: string,
    salesPartnerName: string, isElimsLab: boolean, apiUrl: string) {

    const body: QuotationContractRequest = {
      quotation,
      contract: isElimsLab ? { code: contract.code, name: contract.name }
        : { ExternalLIMSContractCode: contract.code, ExternalLIMSContractName: contract.name },
      receivingPartnerCode: receivingLabCode,
      receivingLabName,
      salesPartnerCode,
      salesLabName: salesPartnerName,
    };

    return this.put(apiUrl + `/quotationContracts`, body);
  }

  addOrUpdateOrderCommentTemplate(orderCode: string, code: string, name: string): Observable<any> {
    const apiUrl = `orders/${orderCode}`;
    const body: OrderCommentTemplateRequest = { code, name };
    return this.put(apiUrl + `/commentTemplate`, body);
  }

  deleteOrderCommentTemplate(orderCode: string): Observable<any> {
    const apiUrl = `orders/${orderCode}`;
    return this.delete(apiUrl + `/commentTemplate`);
  }

  resetInvoiceableItemsComment(orderCode: string): Observable<any> {
    const apiUrl = `orders/${orderCode}/invoiceableItems/resetComment`;
    return this.put(apiUrl, null);
  }

  getSampleCodes(salesPartnerCode: string, count: number): Observable<string[]> {
    if (count <= 0) {
      return of<string[]>([]);
    }

    const apiUrl =
      `labsites/${salesPartnerCode}/labsiteType/1/sampleCodes?count=${count}`;
    return this.get(apiUrl)
      .pipe(map((x: { result: string[] }) => x.result));
  }

  private buildPatchRequest(patchURL: string, updatedProperties: PatchUpdateRequest[]): Observable<any> {

    const body: PatchRequest[] = [];
    updatedProperties.forEach(property => {
      body.push({
        value: property.updatedValue,
        op: 'replace',
        path: `/${property.propertyName}`,
      });
    });

    const apiUrl = patchURL;
    return this.patch<any>(apiUrl, body);
  }
  //#endregion
}

//#region Request / Response models
interface BulkSampleResponse {
  samples?: {
    sampleCode?: number;
    customerReference?: string;
  }[];
}

interface OrderResponse {
  orderId?: string;
  orderReference?: string;
}

interface CreateOrderRequest {
  orderSource?: string;
  labsiteCode?: string;
  labsiteType?: string;
}

class SampleWithAdditionalField {
  code?: number;
  sampleDetails?: SampleDetails;
  additionalFields?: AdditionalFieldDto[] = [];
}

interface CopySampleRequest {
  sourceSampleCode?: string;
  customerReferences?: string[];
  copyOptions?: number[];
}

interface CopySampleResponse {
  samples?: {
    sampleCode?: string;
    customerReferences?: string;
    sampleReference?: string;
  }[];
}

interface BulkSampleResponse {
  sampleCode?: string;
  customerReference?: string;
}

interface PatchRequest {
  value?: any;
  path?: string;
  op?: string;
  from?: string;
}

export interface SamplePatchRequest {
  sampleCode: string;
  colName: string;
  value?: any;
  optype?: string;
}

interface AdditionalFieldPatchRequest {
  sampleCode: string;
  additionalFieldCode: string;
  colName: string;
  value?: any;
  optype?: string;
}

interface OrderDetails {
  name?: string;
}
interface OrderCommentTemplateRequest {
  code: string;
  name: string;
}
//#endregion
